import { memo, useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { clearMessages } from 'store/ancillary/actions';
import { raiseErrorBanner } from 'components/MessageDispatch/messageDispatch';
import Box from '@mui/material/Box';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import FormControl from '@mui/material/FormControl';
import Checkbox from '@mui/material/Checkbox';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import EditIcon from '@mui/icons-material/Edit';
import PersonIcon from '@mui/icons-material/Person';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import { JobCategoryAutocomplete } from './JobCategoryAutocomplete';
import Tooltip from '@mui/material/Tooltip';
import { getDirectReportDetails } from '../_api';
import { OBJ_APP_BRIDGES } from '../_constants';
import { debounce } from '../../../../utils/misc/debounce';

const ValidationRow = memo(function ValidationRow({
	canCreate = true,
	canDelete = false,
	id = '-1',
	loading = false,
	readOnly = false,
	selected = false,
	onCreate = () => {},
	onDelete = () => {},
	onEdit = null,
	onJobCategory = () => {},
	onIconClick = () => {},
	onSelect = () => {},
	onToggle = () => {},
	onValidate = () => {},
	assignmentExperience = false,
	assignmentValid = false,
	assignmentWave = 3,
	assignmentJCID = -1,
	assignmentJCAppID = OBJ_APP_BRIDGES.ID,
	assignmentJCName = '',
	assignmentJCType = '',
	assignmentJCActive = true,
	reportID = -1,
	reportName = '',
	systemID = OBJ_APP_BRIDGES.ID
}) {
	const dispatch = useDispatch();

	const [sReportDetails, setReportDetails] = useState(null);
	const [sValidationMessage, setValidationMessage] = useState('');

	const disabled = readOnly || !!onEdit;
	const isInactiveAssignment = assignmentJCID !== -1 && !assignmentJCActive;
	const isLockedInactiveInEditView = !onEdit && isInactiveAssignment;

	const onChange = useCallback(
		jc => {
			onJobCategory(id, jc)
				.then(() => setValidationMessage(''))
				.catch(message => setValidationMessage(message));
		},
		[id, onJobCategory]
	);

	const onCheck = useMemo(() => {
		return debounce(() => {
			onValidate(reportID, id, assignmentJCID, assignmentValid);
		}, 200);
	}, [reportID, id, assignmentJCID, assignmentValid, onValidate]);

	const onClick = useCallback(() => {
		onEdit(reportID, reportName);
	}, [reportID, reportName, onEdit]);

	const onHover = useCallback(
		async id => {
			try {
				const objDetails = await getDirectReportDetails(id);
				if (objDetails) {
					setReportDetails(objDetails);
				}
			} catch (ex) {
				dispatch(clearMessages());
				raiseErrorBanner(ex, dispatch);
			}
		},
		[dispatch]
	);

	return (
		<>
			<TableRow
				selected={!disabled && selected}
				onClick={() => onSelect(selected ? -1 : !disabled ? id : -1)}
			>
				<td />
				{onEdit && (
					<TableCell
						align='center'
						onClick={e => {
							// clicks in cell must be prevented from bubbling
							// if not, it will be handled as a row selection change and result in a re-render, overwriting attempts to debounce the cell's input changes
							e.stopPropagation();
						}}
					>
						<IconButton disabled={loading} onClick={onClick}>
							<EditIcon />
						</IconButton>
					</TableCell>
				)}
				<TableCell
					sx={{
						color: assignmentValid ? 'success.main' : 'error.main',
						cursor: onEdit ? 'default' : 'unset',
					}}
					align='center'
					padding={onEdit ? 'none' : 'checkbox'}
				>
					{isLockedInactiveInEditView ? (
						<Tooltip
							title='This job category is locked and cannot be edited.'
							placement='top'
						>
							<InfoIcon color='action' fontSize='small' />
						</Tooltip>
					) : onEdit ? (
						assignmentValid ? (
							<b>Yes</b>
						) : (
							<b>No</b>
						)
					) : (
						<FormControl>
							<Checkbox
								color='success'
								disabled={
									!(assignmentJCID !== -1) ||
									(!assignmentValid && // account for a missing wave assignment despite prior validation
										!(assignmentWave !== null)) ||
									!!sValidationMessage ||
									disabled
								}
								checked={assignmentValid ?? false}
								onClick={onCheck}
							/>
						</FormControl>
					)}
				</TableCell>
				<TableCell
					align='left'
					sx={{
						cursor: 'default',
						width: '22.5%',
						maxWidth: '22.5%',
						paddingRight: '8px',
					}}
				>
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'horizontal',
							alignItems: 'center',
						}}
					>
						<Tooltip
							title={
								sReportDetails && (
									<Box>
										Department: {sReportDetails.department}
										<br />
										Job Title: {sReportDetails.jobTitle}
									</Box>
								)
							}
							placement='left'
						>
							<IconButton
								sx={{
									marginRight: '4px',
									marginLeft: '-12.5px',
								}}
								size='small'
								disableTouchRipple={true}
								onMouseEnter={async () => {
									if (!sReportDetails && reportID) {
										await onHover(reportID);
									}
								}}
							>
								<PersonIcon fontSize='small' />
							</IconButton>
						</Tooltip>

						{reportName.replace('NOMI', '')}
					</Box>
				</TableCell>
				<TableCell
					align='left'
					sx={{ width: '52.5%', maxWidth: '52.5%' }}
				>
					<JobCategoryAutocomplete
						jcID={assignmentJCID}
						jcAppID={assignmentJCAppID}
						jcName={assignmentJCName}
						jcType={assignmentJCType}
						onChange={onChange}
						onIconClick={onIconClick}
						readOnly={
							disabled ||
							assignmentValid ||
							!id ||
							isLockedInactiveInEditView
						}
						systemID={systemID}
						validationMessage={sValidationMessage}
					/>
				</TableCell>
				<TableCell align='left' sx={{ width: '20%', maxWidth: '20%' }}>
					<FormControl
						disabled={
							assignmentJCID === -1 ||
							assignmentJCAppID !== OBJ_APP_BRIDGES.ID ||
							assignmentValid ||
							disabled ||
							isLockedInactiveInEditView
						}
						fullWidth
						hiddenLabel
						margin='none'
						variant='standard'
					>
						<Select
							sx={{
								height: '37px',
								padding: '8px 0 1px 0',
								'& .MuiSelect-select': {
									'&:focus': { backgroundColor: 'unset' },
									padding: '2px 0 3px 0',
								},
							}}
							label=''
							onChange={e => {
								onToggle(id, e?.target?.value);
							}}
							SelectDisplayProps={{
								sx: {
									padding: '2px 0 3px 0',
								},
							}}
							value={
								assignmentJCAppID !== OBJ_APP_BRIDGES.ID
									? false
									: assignmentExperience !== null
										? assignmentExperience
										: false
							}
						>
							<MenuItem value={false}>New</MenuItem>
							<MenuItem value={true}>Experienced</MenuItem>
						</Select>
					</FormControl>
				</TableCell>
				<TableCell align='left' sx={{ width: '5%', maxWidth: '5%' }}>
					<FormControl
						disabled={
							assignmentJCID === -1 ||
							assignmentJCAppID !== OBJ_APP_BRIDGES.ID ||
							assignmentValid ||
							disabled ||
							isLockedInactiveInEditView
						}
						fullWidth
						hiddenLabel
						margin='none'
						variant='standard'
					>
						<Select
							sx={{
								height: '37px',
								padding: '8px 0 1px 0',
								'& .MuiSelect-select': {
									'&:focus': { backgroundColor: 'unset' },
									padding: '2px 0 3px 0',
								},
							}}
							label=''
							onChange={e => onToggle(id, e?.target?.value)}
							SelectDisplayProps={{
								sx: {
									padding: '2px 0 3px 0',
								},
							}}
							value={
								assignmentWave !== null ? assignmentWave : ''
							}
						>
							<MenuItem value={-1} sx={{ display: 'none' }}>
								-1
							</MenuItem>
							<MenuItem value={1}>1</MenuItem>
							<MenuItem value={2}>2</MenuItem>
							<MenuItem value={3}>3</MenuItem>
						</Select>
					</FormControl>
				</TableCell>
				<td>
					{!disabled && selected && !isLockedInactiveInEditView && (
						<IconButton
							aria-label='Remove'
							title='Remove'
							sx={{
								position: 'absolute',
								marginTop: '-17px',
							}}
							size='small'
							disabled={!canDelete || disabled}
							onClick={() => onDelete(id)}
						>
							<DeleteIcon />
						</IconButton>
					)}
				</td>
			</TableRow>
			{!disabled && selected && (
				<tr>
					<td>
						<IconButton
							aria-label='Create New'
							title='Create New'
							sx={{
								position: 'absolute',
								marginTop: '-15px',
								marginLeft: '-30px',
							}}
							size='small'
							disabled={
								!canCreate || assignmentJCID === -1 || disabled
							}
							onClick={() => {
								onCreate(id);
								onSelect(-1);
							}}
						>
							<AddCircleIcon fontSize='small' />
						</IconButton>
					</td>
				</tr>
			)}
		</>
	);
});

export { ValidationRow };
